import assert from 'assert';
import { nanoid } from 'nanoid';
import React, { Component } from 'react';
import track from 'react-tracking';
import { GlobalContext } from '../../interfaces/GlobalContext/GlobalContext';
import InfoButton from '../../interfaces/InfoButton/InfoButton';
import Option from '../../interfaces/Option/Option';
import ProductProperties, { FormFieldName, FormFieldType } from '../../interfaces/ProductProperties/ProductProperties';
import ProductPropertiesCheckbox from '../../interfaces/ProductProperties/ProductPropertiesCheckbox';
import { ProductPropertiesFieldConfigurations } from '../../interfaces/ProductProperties/ProductPropertiesFieldConfigurations';
import ProductPropertiesQueryInformation from '../../interfaces/ProductProperties/ProductPropertiesQueryInformation';
import { ProductType } from '../../interfaces/ProductProperties/ProductPropertiesRelated';
import ProductPropertiesSelect from '../../interfaces/ProductProperties/ProductPropertiesSelect';
import ProductPropertiesSelectOption from '../../interfaces/ProductProperties/ProductPropertiesSelectOption';
import TrackingMeta from '../../interfaces/Tracking/TrackingMeta';
import { TrackingVariantETracker } from '../../interfaces/Tracking/TrackingVariantETracker';
import { TrackingVariantICE } from '../../interfaces/Tracking/TrackingVariantICE';
import FormField from '../FormField/FormField';
import FormFieldChooseGroup from '../FormFieldChooseGroup/FormFieldChooseGroup';
import FormFieldHidden from '../FormFieldHidden/FormFieldHidden';
import FormFieldInfoMessage from '../FormFieldInfoMessage/FormFieldInfoMessage';
import FormFieldRadio from '../FormFieldRadio/FormFieldRadio';
import FormFieldSelect from '../FormFieldSelect/FormFieldSelect';
import ProductPropertiesOverlayProps from './ProductPropertiesOverlay.props';
import ProductPropertiesOverlayState from './ProductPropertiesOverlay.state';
import ProductPrice from '../../interfaces/ProductPrice/ProductPrice';

interface Selected {
    value?: string,
    name?: string
}

@track({})
export default class ProductPropertiesOverlay extends Component<ProductPropertiesOverlayProps, ProductPropertiesOverlayState> {
    static contextType = GlobalContext;
    context!: React.ContextType<typeof GlobalContext>;
    private formElement: React.RefObject<HTMLFormElement>;
    private cartButton: React.RefObject<HTMLButtonElement>;
    private formId: string;
    private productPropertiesFieldConfigurations: ProductPropertiesFieldConfigurations;
    private frameworkContractChosen = 0; // setting to check wether or not the framework was chosen

    constructor(props: ProductPropertiesOverlayProps) {
        super(props);

        this.state = {
            price: {
                magicNumber: this.props.magicNumber,
                paymentPeriod: '1M',
                price: 0.0,
                priceInclMwst: 0.0,
            },
            updateProductInformation: false,
            magicNumber: this.props.magicNumber,
            campaignId: this.props.campaignId,
            chosenContract: ''
        };

        this.productPropertiesFieldConfigurations = {
            [FormFieldName.CUSTOMEROFOTHERPRODUCTS]: {
                required: false,
            },
            [FormFieldName.CUSTOMEROFPRODUCTSPROFDAVSPECKOMM]: {
                required: false,
            },
            [FormFieldName.JOBBRANCH]: {
                required: true,
            },
            [FormFieldName.MEMBEROFDNOTV]: {
                required: true,
            },
            [FormFieldName.MEMEBEROFDAV]: {
                required: true,
            },
            [FormFieldName.MUNICIPALEMPLOYEE]: {
                required: true,
            },
            [FormFieldName.NUMBEROFDAVUSERS]: {
                required: true,
            },
            [FormFieldName.NUMBEROFLICENCES]: {
                required: true,
            },
            [FormFieldName.PAYMENTPERIOD]: {
                required: true,
            },
            [FormFieldName.PRECONDITIONSELFDISCLOSURE]: {
                required: true,
            },
            [FormFieldName.PREMIUM]: {
                required: true,
            },
            [FormFieldName.PRODUCTVARIANT]: {
                required: true,
            },
            [FormFieldName.FRAMEWORKCONTRACT]: {
                required: true,
            },
        };
        this.formElement = React.createRef<HTMLFormElement>();
        this.cartButton = React.createRef<HTMLButtonElement>();
        this.formId = nanoid();
        this.handleAddToCart = this.handleAddToCart.bind(this);
        this.handleDirectPurchase = this.handleDirectPurchase.bind(this);
        this.closeOverlay = this.closeOverlay.bind(this);
        this.getPrice = this.getPrice.bind(this);
        this.updatePrice = this.updatePrice.bind(this);
        // this.getDiscountPrice = this.getDiscountPrice.bind(this);
        this.changeMagicNumber = this.changeMagicNumber.bind(this);
        this.handleBuyOfTestProduct = this.handleBuyOfTestProduct.bind(this);
        this.submitHandler = this.submitHandler.bind(this);
        this.getTextPriceContent = this.getTextPriceContent.bind(this);
        this.setPaymentPeriod = this.setPaymentPeriod.bind(this);
    }

    componentDidMount() {
        try {
            window.Juris.moduleRegistry.autoApplyModule('Tooltip', '[data-tooltip]');
        } catch (error) {
            console.log(error);
        }

        this.getProductPropertiesFromServerOrLocalStorage(true, true);
        this.context.utilityProvider.dispatchEventReactAppInitialized();
    }

    async getProductPropertiesFromServerOrLocalStorage(emit = false, initial = false) {
        // In direct purchase mode, get product from session storage, otherwise use local storage
        let productPropertiesFromStorage;
        if (this.props.isDirectPurchase) {
            const directPurchaseData = this.context?.sessionStorageProvider.getDirectPurchaseFromSessionStorage();
            if (directPurchaseData && directPurchaseData.productProperties.magicNumber === this.state.magicNumber) {
                productPropertiesFromStorage = { 
                    productProperties: directPurchaseData.productProperties, 
                    warenkorbData: [directPurchaseData.productProperties] 
                };
            } else {
                productPropertiesFromStorage = undefined;
            }
        } else {
            productPropertiesFromStorage = this.context?.localStorageProvider.getProductInformationFromLocalStorage(this.state.magicNumber);
        }
        
        const productPropertiesFromServer = await this.getProductPropertiesFromServer();
        const productExistsInLS = productPropertiesFromStorage?.productProperties !== undefined;

        if (productPropertiesFromServer === undefined || productPropertiesFromServer instanceof Response) {
            this.setState({
                magicNumber: ''
            });
            return;
        }
        
        const inLSExistsBlacklistOrRelatedProductFromServer = this.context.utilityProvider
            .inLSExistsBlacklistOrRelatedProductFromThisProduct(productPropertiesFromServer, productPropertiesFromStorage);

        if (productPropertiesFromServer.queryInformation === undefined) {
            if (this.state.productProperties && this.state.productProperties.queryInformation
                && (this.state.productProperties.productType === ProductType.VARIANT
                    || this.state.productProperties.productType === ProductType.FOR_LAWYER
                    || this.state.productProperties.productType === ProductType.FOR_OTHER)) {
                productPropertiesFromServer.queryInformation = this.state.productProperties.queryInformation;
            } else {
                productPropertiesFromServer.queryInformation = await this.getProductInformationFromQuery(
                    productPropertiesFromServer.magicNumber,
                    productPropertiesFromServer.productType === ProductType.TEST
                );
            }
        }

        if (initial === true){
            this.setState({
                productProperties: productPropertiesFromStorage?.productProperties || productPropertiesFromServer,
                initialProductProperties: productPropertiesFromStorage?.productProperties || productPropertiesFromServer,
                updateProductInformation: productExistsInLS || inLSExistsBlacklistOrRelatedProductFromServer,
                isTestProduct: (productPropertiesFromStorage?.productProperties || productPropertiesFromServer).productType === ProductType.TEST
            }, async () => {
                await this.getPrice(emit);
            });    
        } else {
            this.setState({
                productProperties: productPropertiesFromStorage?.productProperties || productPropertiesFromServer,
                updateProductInformation: productExistsInLS || inLSExistsBlacklistOrRelatedProductFromServer,
                isTestProduct: (productPropertiesFromStorage?.productProperties || productPropertiesFromServer).productType === ProductType.TEST
            }, async () => {
                await this.getPrice(emit);
            });    
        }
      
        // console.log('ProductProperties: ', productPropertiesFromServer)
    }

    getProductPropertiesFromServer(magicNumber?: string): Promise<ProductProperties | Response> {
        const url = `/order-api/getOrderInformation/${magicNumber ? magicNumber : this.state.magicNumber}`;
        // const token = window.localStorage.getItem('accessToken') || undefined;
        return this.context?.requestProvider.getData(url);
    }

    async getProductInformationFromQuery(magicNumber?: string, isTestProduct?: boolean): Promise<ProductPropertiesQueryInformation> {
        const baseUrl = isTestProduct || this.state.isTestProduct
            ? '/delivery-service/select/de?q=shopTestProductId_s:'
            : '/delivery-service/select/de?q=shopProductId_s:'
        const url = baseUrl + (magicNumber || this.state.magicNumber);
        const queryInformation = await this.context?.requestProvider.getData(url);
        return {
            title: queryInformation.response.docs[0]?.IN_NAME as string || '',
            imageAlt: queryInformation.response.docs[0]?.imageAlt_s as string || '',
            imageSrc: queryInformation.response.docs[0]?.image_s as string || '',
            href: (queryInformation.response.docs[0]?.IN_LINK as string) || undefined,
        } as ProductPropertiesQueryInformation;
    }

    async getPrice(emit = false, selected?: string) {
        const productProperties = await this.getProductPropertiesFromForm();
        const selectedOption = this.context.globalStates.getSelectedFrameworkContract() as Selected;
        const price = await this.context.utilityProvider.getPrice(productProperties);

        if (!productProperties) return;

        // console.log('filter: ', productProperties.configOptions?.find(option => option.name === FormFieldName.FRAMEWORKCONTRACT))

        if (selectedOption && selectedOption.name !== FormFieldName.FRAMEWORKCONTRACT) {
    
            if (!productProperties ||
                (productProperties.productType !== ProductType.TEST
                    && (price.price === undefined || price.priceInclMwst === undefined))) {
                this.setState({
                    magicNumber: '',
                });
                return;
            }
    
            this.setState({
                productProperties: productProperties,
                price: price,
            }, () => {
                this.props.emitPrice
                    && emit
                    && this.props.emitPrice(this.state.price);
            });
            return;
        } else {
            this.updatePrice(productProperties, emit, price);
            return;
        }
    }

    async updatePrice(productProperties: ProductProperties, emit = false, price: ProductPrice) {
        // const price = await this.context.utilityProvider.getPrice(productProperties);
        const relatedProduct = productProperties?.relatedProducts.find((item) => item.productType === ProductType.VARIANT);
        const input = this.context.globalStates.getSelectedFrameworkContract() as Selected;

        if (this.state.chosenContract === input.value || input.value === undefined) return;

        if (this.state.chosenContract === '') {
            this.setState({
                chosenContract: input.value,
                price: price
            }, () => {
                this.props.emitPrice
                    && emit
                    && this.props.emitPrice(this.state.price);
            })
            return;
        }

        if (input.value.toString() === this.state.chosenContract.toString()) {
            this.setState({
                productProperties: productProperties,
                price: price,
                // chosenContract: input.value
            }, () => {
                this.props.emitPrice
                    && emit
                    && this.props.emitPrice(this.state.price);
            });
            return;
        } 
        if (input.value.toString() !== this.state.chosenContract.toString()) {
            const discountProperties =  await this.getProductPropertiesFromServer(relatedProduct?.magicNumber) as ProductProperties;

            const formFieldNumberOfLicensesBeforeChosenContract = productProperties.configOptions?.find(option => option.name === FormFieldName.NUMBEROFLICENCES);
            const formFieldNumberOfDavUsersBeforeChosenContract = productProperties.configOptions?.find(option => option.name === FormFieldName.NUMBEROFDAVUSERS);
            const formFieldPaymentPeriodBeforeChosenContract = productProperties.configOptions?.find(option => option.name === FormFieldName.PAYMENTPERIOD);

            const formFieldNumberOfLicenses = discountProperties.configOptions?.find(option => option.name === FormFieldName.NUMBEROFLICENCES);
            const formFieldNumberOfDavUsers = discountProperties.configOptions?.find(option => option.name === FormFieldName.NUMBEROFDAVUSERS);
            const formFieldPaymentPeriod = discountProperties.configOptions?.find(option => option.name === FormFieldName.PAYMENTPERIOD);

            if (formFieldNumberOfLicensesBeforeChosenContract && formFieldNumberOfLicenses) {
                formFieldNumberOfLicenses.value = formFieldNumberOfLicensesBeforeChosenContract.value;
            }

            if (formFieldNumberOfDavUsersBeforeChosenContract && formFieldNumberOfDavUsers) {
                formFieldNumberOfDavUsers.value = formFieldNumberOfDavUsersBeforeChosenContract.value;
            }

            if (formFieldPaymentPeriodBeforeChosenContract && formFieldPaymentPeriod) {
                formFieldPaymentPeriod.value = formFieldPaymentPeriodBeforeChosenContract.value;
            }

            if (formFieldNumberOfDavUsers
                && formFieldNumberOfLicenses
                && Number(formFieldNumberOfLicenses.value) < Number(formFieldNumberOfDavUsers.value)) {
                formFieldNumberOfDavUsers.value = formFieldNumberOfLicenses.value;
            }

            const discountPrice = await this.context.utilityProvider.getPrice(discountProperties);

            if (discountProperties.queryInformation === undefined) {
                discountProperties.queryInformation = await this.getProductInformationFromQuery();
            }

            discountProperties.configOptions = productProperties.configOptions?.map((formField) => {
                if (formField.name === FormFieldName.FRAMEWORKCONTRACT) {
                    // if (input.value === 'andererVerband') {
                    //     formField.value = 'nein'
                    // } 
                    // else {
                    formField.value = input.value as string;
                    // }
                }
                return formField;
            });
            
            if (this.state.chosenContract !== 'nein' && this.state.chosenContract !== 'andererVerband' && (input.value === 'nein' || input.value === 'andererVerband')) {
                this.context.globalStates.setIsDiscountProductOfFrameworkContracts(true);
                this.setState({
                    productProperties: discountProperties,
                    price: discountPrice,
                    chosenContract: input.value,
                }, () => {
                    this.props.emitPrice
                        && emit
                        && this.props.emitPrice(this.state.price);
                });



            } else if ((this.state.chosenContract === 'nein' || this.state.chosenContract === 'andererVerband') && input.value !== 'nein' && input.value !== 'andererVerband') {
                this.context.globalStates.setIsDiscountProductOfFrameworkContracts(false);
                this.setState({
                    productProperties: discountProperties,
                    price: discountPrice,
                    chosenContract: input.value,
                }, () => {
                    this.props.emitPrice
                        && emit
                        && this.props.emitPrice(this.state.price);
                });
            }
            return;
        }
    }

    isFormValid(): boolean {
        let formIsValid = true;
        for (const key in this.productPropertiesFieldConfigurations) {
            const fieldIsValid = this.productPropertiesFieldConfigurations[key as FormFieldName].ref?.isValid();
            if (fieldIsValid === false) {
                formIsValid = false;
                this.productPropertiesFieldConfigurations[key as FormFieldName].ref?.toggleValidationError(true);
            } else {
                this.productPropertiesFieldConfigurations[key as FormFieldName].ref?.toggleValidationError(false);
            }
        }

        this.setState({
            formIsValid: formIsValid,
        });
        return formIsValid;
    }

    trackAddToCart(shouldNotTrackAddToCart: boolean) {
        if (!shouldNotTrackAddToCart) {
            this.trackAddToCartETracker();
            this.trackAddToCartICE();
        }
    }

    @track((props: ProductPropertiesOverlayProps, state: ProductPropertiesOverlayState) => ({
        trackingMeta: {
            variant: TrackingVariantICE.ADDTOCART
        } as TrackingMeta,
        dyType: "add-to-cart-v1",
        value: state.price.price,
        currency: "EUR",
        productId: state.magicNumber,
        quantity: state.productProperties?.configOptions?.find((option) => {
            return option.name === FormFieldName.NUMBEROFLICENCES;
        })?.value || '1',
        productProperties: state.productProperties
    }))
    trackAddToCartICE() {
        return;
    }

    @track((props: ProductPropertiesOverlayProps, state: ProductPropertiesOverlayState) => ({
        trackingMeta: {
            variant: TrackingVariantETracker.INSERTTOBASKET
        } as TrackingMeta,
        id: state.magicNumber,
        name: state.productProperties?.title || 'NOTITLE',
        category: [
            state.productProperties?.productType  
        ],
        price: state.price.price?.toFixed(2) || '0',
        currency: 'EUR',
        variants: {},
        quantity: parseInt(state.productProperties?.configOptions?.find((option) => {
            return option.name === FormFieldName.NUMBEROFLICENCES;
        })?.value || '1', 10),
    }))
    trackAddToCartETracker() {
        return;
    }

    async handleAddToCart(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        e.stopPropagation();
        if (this.isFormValid()) {
            const productProperties = await this.getProductPropertiesFromForm() as ProductProperties;
            
            productProperties.configOptions?.forEach((formField) => {
                if (formField.name === FormFieldName.FRAMEWORKCONTRACT) {
                    if (formField.value === 'andererVerband') {
                        formField.value = 'nein'
                    }
                }
            });

            const productAlreadyExistsInCart = this.context.utilityProvider.inLSExistsBlacklistOrRelatedProductFromThisProduct(productProperties);
            this.setState({
                productProperties: productProperties,
                updateProductInformation: true
            }, async () => {
                this.addToCart(productAlreadyExistsInCart);
                await this.getPrice(true);
            });
        }
    }

    async handleDirectPurchase(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        e.stopPropagation();
        
        if (this.isFormValid()) {
            // Clear any existing direct purchase data
            this.context.sessionStorageProvider.clearDirectPurchaseFromSessionStorage();
            
            // Get product properties from form
            const productProperties = await this.getProductPropertiesFromForm() as ProductProperties;
            
            if (productProperties) {
                // Process config options similar to regular add to cart
                productProperties.configOptions?.forEach((formField) => {
                    if (formField.name === FormFieldName.FRAMEWORKCONTRACT) {
                        if (formField.value === 'andererVerband') {
                            formField.value = 'nein'
                        }
                    }
                });

                // Save to session storage with campaign ID
                this.context.sessionStorageProvider.setDirectPurchaseInSessionStorage(
                    productProperties, 
                    this.state.campaignId // Pass campaign ID from component state
                );
                
                // Close overlay
                this.closeOverlay();
                
                // Navigate to cart page with direct purchase mode
                const cartPageUrl = this.context.i18nProvider.get('generalRessources.cartPageUrl');
                if (cartPageUrl) {
                    window.location.href = cartPageUrl;
                }
            }
        }
    }

    async getProductPropertiesFromForm(): Promise<ProductProperties | undefined> {
        const formData: Record<string, string> = {};
        for (const key in this.productPropertiesFieldConfigurations) {
            const fieldValue = this.productPropertiesFieldConfigurations[key as FormFieldName].ref?.getValue();
            if (fieldValue !== undefined) {
                formData[key as FormFieldName] = fieldValue;
            } else {
                delete formData[key as FormFieldName];
            }
        }
        const productProperties = this.state.productProperties;

        if (!productProperties) {
            return undefined;
        }

        const formFieldNumberOfLicenses = productProperties.configOptions?.find(option => option.name === FormFieldName.NUMBEROFLICENCES);
        const formFieldNumberOfDavUsers = productProperties.configOptions?.find(option => option.name === FormFieldName.NUMBEROFDAVUSERS);

        // MARTP-1168 testProduct with configOption (frameworkContract)
        // if (!this.state.isTestProduct) {
        productProperties.configOptions = productProperties.configOptions?.map((formField) => {
            // if (formField.name !== FormFieldName.FRAMEWORKCONTRACT && this.state.isTestProduct) return;
            formField.value = formData[formField.name] as string;
            return formField;
        });
        // }

        if (formFieldNumberOfDavUsers
            && formFieldNumberOfLicenses
            && Number(formFieldNumberOfLicenses.value) < Number(formFieldNumberOfDavUsers.value)) {
            formFieldNumberOfDavUsers.value = formFieldNumberOfLicenses.value;
        }

        // if (formFieldFrameworkContracs) {
        //     console.log('frameworks: ', productProperties)
        // }

        if (productProperties.queryInformation === undefined) {
            productProperties.queryInformation = await this.getProductInformationFromQuery();
        }

        return productProperties;
    }

    closeOverlay() {
        this.props.closeButtonRef?.current?.click();
    }

    createFormFieldSelect(formField: ProductPropertiesSelect): React.ReactElement<FormFieldSelect> {
        let textOptions = this.context.i18nProvider.get(
            `productPropertiesOverlay.${formField.name}.choices`,
            {
                returnObjects: true
            }
        ) as unknown as Array<ProductPropertiesSelectOption>;

        const filteredTextOptionsForFrameworkContract: Array<ProductPropertiesSelectOption> = [];
        const sortedTextOptionsForFrameworkContract: Array<ProductPropertiesSelectOption> = [];

        const formFieldIsNUMBEROFDAVUSERS = formField.name === FormFieldName.NUMBEROFDAVUSERS;
        const formFieldNUMBEROFLICENCES = this.state.productProperties?.configOptions?.find(option => option.name === FormFieldName.NUMBEROFLICENCES);
        const formFieldIsNumberOfLicenses = formField.name === FormFieldName.NUMBEROFLICENCES;
        const formFieldIsPaymentPeriods = formField.name === FormFieldName.PAYMENTPERIOD;
        const formFieldIsFRAMEWORKCONTRACT = formField.name === FormFieldName.FRAMEWORKCONTRACT;
        const formFieldFrameworkContract = this.state.productProperties?.configOptions?.find(option => option.name === FormFieldName.FRAMEWORKCONTRACT);

        // MARTP-1168: filtering textOptions in order to add only if frameworkcontract is part of product
        if (formFieldIsFRAMEWORKCONTRACT) {
            // console.log('formFieldFrameworkContract: ', formFieldFrameworkContract?.choices);
            // console.log('textOptions: ', textOptions);

            formFieldFrameworkContract?.choices?.forEach(choice => {
                textOptions.filter(textOption => {
                    if (choice === textOption.value) {
                        filteredTextOptionsForFrameworkContract.push(textOption)
                    }
                    return choice === textOption.value
                })
            })

            filteredTextOptionsForFrameworkContract?.sort((a: ProductPropertiesSelectOption, b: ProductPropertiesSelectOption) => a.label && b.label ? a.label.localeCompare(b.label) : 0);
            filteredTextOptionsForFrameworkContract?.forEach(option => {
                if(option.value !== 'nein' && option.value !== 'andererVerband') {
                    sortedTextOptionsForFrameworkContract.push(option);
                }
            })
            sortedTextOptionsForFrameworkContract.unshift({
                label: "nein",
                value: "nein"
            })
            sortedTextOptionsForFrameworkContract.push({
                label: "anderer Verband",
                value: "andererVerband"
            })

            textOptions = sortedTextOptionsForFrameworkContract;
            // console.log('textOptions: ', textOptions)
        }

        let hidden = false;
        if (formFieldIsNumberOfLicenses && (formField as ProductPropertiesSelect).choices.length === 1) {            
            hidden = true;
        }
        if (formFieldFrameworkContract && (formField as ProductPropertiesSelect).choices.length === 1) {
            hidden = true;
        }

        let hint = undefined;
        if (formFieldIsNumberOfLicenses && this.state.productProperties?.professionalContract) {
            hint = this.context.i18nProvider.get(`productPropertiesOverlay.professionalContract`);
        }

        // console.log('options: ', filteredTextOptionsForFrameworkContract.length);
        // console.log('formFieldIsPaymentPeriods: ',formFieldIsPaymentPeriods);
        
        

        return <FormFieldSelect id={this.state.magicNumber + formField.name}
            name={formField.name}
            label={this.context.i18nProvider.get(`productPropertiesOverlay.${formField.name}.label`)}
            multiple={false}
            required={formField.required}
            selected={formField.value}
            hidden={hidden}
            info={this.context.i18nProvider.exists(`productPropertiesOverlay.${formField.name}.tooltip`) ? {
                text: this.context.i18nProvider.get(`productPropertiesOverlay.${formField.name}.tooltip`)
            } as InfoButton : undefined}
            hint={hint}
            options={
                textOptions.filter(textOption => {
                    if (formField.choices.indexOf(String(textOption.value)) < 0) {
                        return false;
                    }

                    if (formFieldIsNUMBEROFDAVUSERS && formFieldNUMBEROFLICENCES) {
                        return Number(textOption.value) <= Number(formFieldNUMBEROFLICENCES.value);
                    }

                    return true;
                }).map(textOption => {
                    if (textOption?.label && textOption?.value) {
                        return {
                            label: textOption.label,
                            value: String(textOption.value),
                        }
                    } else if (textOption?.name && textOption?.value) {
                        return {
                            label: textOption.name,
                            value: String(textOption.value),
                        }
                    }
                    return {
                        label: '',
                        value: '',
                    }
                })
            }
            onChange={this.getPrice}
            onChangePaymentPeriod={formFieldIsPaymentPeriods ? this.setPaymentPeriod : undefined}
            key={this.state.magicNumber + formField.name}
            ref={instance => this.productPropertiesFieldConfigurations[formField.name].ref = instance || undefined} />

    }

    setPaymentPeriod(paymentPeriod: string) {
        this.setState({
            paymentPeriod: paymentPeriod,
        }, () => this.getTextPriceContent())
    }

    createFormFieldRadio(formField: ProductPropertiesCheckbox): React.ReactElement<FormFieldRadio> {
        return <FormFieldRadio id={this.state.magicNumber + formField.name}
            isInPPO={true}
            name={formField.name}
            info={this.context.i18nProvider.exists(`productPropertiesOverlay.${formField.name}.tooltip`) ? {
                text: this.context.i18nProvider.get(`productPropertiesOverlay.${formField.name}.tooltip`)
            } as InfoButton : undefined}
            label={this.context.i18nProvider.get(`productPropertiesOverlay.${formField.name}.label`)}
            required={formField.required}
            selected={String(formField.value)}
            options={this.context.i18nProvider.get(`productPropertiesOverlay.${formField.name}.choices`, {
                returnObjects: true
            }) as unknown as Option[]}
            onChange={this.getPrice}
            key={this.state.magicNumber + formField.name}
            ref={instance => this.productPropertiesFieldConfigurations[formField.name].ref = instance || undefined} />
    }

    createFormFieldCheckbox(formField: ProductPropertiesCheckbox): React.ReactElement<FormFieldChooseGroup> {
        const textOptions = this.context.i18nProvider.get(
            `productPropertiesOverlay.${formField.name}.choices`,
            { returnObjects: true }
        ) as unknown as Array<ProductPropertiesSelectOption>;

        return <FormField key={this.state.magicNumber + formField.name}
            errorMessage={this.context.i18nProvider.get(`productPropertiesOverlay.${formField.name}.errorMessage`)}
            info={this.context.i18nProvider.exists(`productPropertiesOverlay.${formField.name}.tooltip`) ? {
                text: this.context.i18nProvider.get(`productPropertiesOverlay.${formField.name}.tooltip`)
            } as InfoButton : undefined}
            isInPPO={true}>
            <FormFieldChooseGroup id={this.state.magicNumber + formField.name}
                name={formField.name}
                label={this.context.i18nProvider.exists(`productPropertiesOverlay.${formField.name}.label`)
                    ? this.context.i18nProvider.get(`productPropertiesOverlay.${formField.name}.label`)
                    : undefined}
                options={textOptions.map((option) => {
                    return {
                        label: option.name,
                        value: option.value
                    };
                })}
                value={formField.value || undefined}
                required={formField.required}
                mustBeTrue={formField.required}
                optionStyle={'checkbox'}
                alternativeStyle={true}
                onChange={() => this.getPrice()}
                shouldReturnFalseThanUndefinedIfNotChecked={true}
                ref={instance => this.productPropertiesFieldConfigurations[formField.name].ref = instance || undefined} />
        </FormField>
    }

    enumKeys<O extends object, K extends keyof O = keyof O>(obj: O): K[] {
        return Object.keys(obj).filter(k => Number.isNaN(+k)) as K[];
    }

    createFieldIfExist(): React.ReactElement[] | null {
        if (!this.state.productProperties) {
            return null;
        }

        const result: React.ReactElement[] = [];
        for (const formFieldName of this.enumKeys(FormFieldName)) {
            const formField = this.state.productProperties?.configOptions?.find(
                formField => formField.name === FormFieldName[formFieldName]
            );

            if (formField) {
                switch (formField.type) {
                    case FormFieldType.SINGLESELECT:
                        // console.log('single seleft: ', formField)
                        result.push(this.createFormFieldSelect(formField as ProductPropertiesSelect));
                        break;
                    case FormFieldType.BOOLEAN:
                        if (formField.name === FormFieldName.PRECONDITIONSELFDISCLOSURE
                            || formField.name === FormFieldName.CUSTOMEROFOTHERPRODUCTS
                            || formField.name === FormFieldName.MUNICIPALEMPLOYEE
                            || formField.name === FormFieldName.FRAMEWORKCONTRACT
                            || formField.name === FormFieldName.CUSTOMEROFPRODUCTSPROFDAVSPECKOMM) {
                            result.push(this.createFormFieldCheckbox(formField as ProductPropertiesCheckbox));
                        } else {
                            result.push(this.createFormFieldRadio(formField as ProductPropertiesCheckbox));
                        }
                        break;
                }
            }
        }

        return result;
    }

    // submitHandler(e: React.FormEvent<HTMLFormElement>) {
    //     console.log('SUBMITHANDLER');
    //     this.saveCampaignIdToLocalStorage();
    //     e.preventDefault();
    //     window.Juris.moduleRegistry._registeredModules.get("CartFlyout").instances.entries().next().value
    //         && window.Juris.moduleRegistry._registeredModules.get("CartFlyout").instances.entries().next().value[1]._toggleFlyout();
    //     this.closeOverlay();
    // }

    submitHandler() {
        // this.saveCampaignIdToLocalStorage();
        this.saveCampaignIdToSessionStorage();
    }

    handleBuyOfTestProduct() {
        // this.saveCampaignIdToLocalStorage();
        this.saveCampaignIdToSessionStorage();

        this.state.productProperties?.configOptions?.forEach((formField) => {
            if (formField.name === FormFieldName.FRAMEWORKCONTRACT) {
                if (formField.value === 'andererVerband') {
                    formField.value = 'nein'
                }
            }
        });

        const productAlreadyExistsInCart = this.context.utilityProvider.inLSExistsBlacklistOrRelatedProductFromThisProduct(this.state.productProperties);
        this.setState({
            updateProductInformation: true
        }, async () => {
            this.addToCart(productAlreadyExistsInCart);
        });
    }


    saveCampaignIdToLocalStorage() {
        // console.log('saveCampaignId');
        if (this.state.campaignId){
            this.context?.localStorageProvider.setCampaignInLocalStorage(this.state.campaignId);
        }
    }

    saveCampaignIdToSessionStorage() {
        // console.log('saveCampaignId');
        if (this.state.campaignId){
            this.context?.sessionStorageProvider.setCampaignInSessionStorage(this.state.campaignId);
        }
    }

    addToCart(shouldNotTrackAddToCart: boolean) {
        // console.log('add to cart')
        assert(this.state.productProperties);
        if (!this.context.utilityProvider.inLSExistsSameProductAsThisProduct(this.state.productProperties)) {
            this.trackAddToCart(shouldNotTrackAddToCart);
        }
        this.context?.localStorageProvider.setProductInformationInLocalStorage(this.state.productProperties);
        this.closeOverlay();
        try {
            if (window.Juris.moduleRegistry._registeredModules.get("CartFlyout").instances.entries().next().value) {
                // console.log('addtoCart flyout: ', window.Juris.moduleRegistry._registeredModules.get("CartFlyout").instances.entries())
                window.Juris.moduleRegistry._registeredModules.get("CartFlyout").instances.entries().next().value[1]._renderProducts();
                window.Juris.moduleRegistry._registeredModules.get("CartFlyout").instances.entries().next().value[1]._toggleFlyout(true);
            }
        } catch (error) {
            this.context.logger.error("CartFlyout had an error.", error);
        }

        try {
            if (window.Juris.moduleRegistry._registeredModules.get("Navigation").instances.entries().next().value) {
                window.Juris.moduleRegistry._registeredModules.get("Navigation").instances.entries().next().value[1]._updateCounter();
            }
        } catch (error) {
            this.context.logger.info("No Navigation on the page.", error);
        }
    }

    changeMagicNumber(changeToMagicNumber?: string) {
        assert(this.state.productProperties);
        this.setState({
            magicNumber: changeToMagicNumber
                || this.context.utilityProvider.getPremiumProductFromProductProperties(this.state.productProperties)?.magicNumber
                || this.context.utilityProvider.getBaseProductFromProductProperties(this.state.productProperties)?.magicNumber
                || this.context.utilityProvider.getForLawyerProductFromProductProperties(this.state.productProperties)?.magicNumber
                || this.context.utilityProvider.getForOtherProductFromProductProperties(this.state.productProperties)?.magicNumber
                || this.state.magicNumber
        }, this.getProductPropertiesFromServerOrLocalStorage);
    }

    // if needed to place dropdown in right position
    renderTaxassosiation() {
        assert(this.state.productProperties);

    }

    renderPremiumField() {
        assert(this.state.productProperties);
        assert(this.state.initialProductProperties);
        const productIsPremiumProduct = this.state.productProperties.productType === ProductType.PREMIUM;
        const initialProductIsPremiumProduct = this.state.initialProductProperties.productType === ProductType.PREMIUM;
        const initialProductHasPremiumProduct = this.context.utilityProvider.getPremiumProductFromProductProperties(this.state.initialProductProperties);

        // console.log('productProperties', this.state.productProperties);
        // console.log('initialProductProperties', this.state.initialProductProperties);

        if (initialProductIsPremiumProduct || !initialProductHasPremiumProduct) {
            return <></>
        }
        
        return <FormFieldRadio name={FormFieldName.PREMIUM}
            id={this.state.magicNumber + FormFieldName.PREMIUM}
            label={this.context.i18nProvider.get(`productPropertiesOverlay.${FormFieldName.PREMIUM}.label`)}
            labelSubElement={
                <>
                    <span
                        className="text-icon-link text-icon-link--link"
                        style={{cursor: 'pointer', marginTop:'0.15rem'}}                        
                        onClick={() => document.getElementById("compare_link")?.click()}
                    >
                        <span className="text-icon-link__icon"></span>
                        <span className="text-icon-link__text">
                            {this.context.i18nProvider.get(`productPropertiesOverlay.${FormFieldName.PREMIUM}.textCompareLink`)}
                        </span>
                    </span>
                </>
            }
            selected={String(productIsPremiumProduct)}
            options={this.context.i18nProvider.get(`productPropertiesOverlay.${FormFieldName.PREMIUM}.choices`, {
                returnObjects: true,
            }) as unknown as Option[]}
            isInPPO={true}
            info={this.context.i18nProvider.exists(`productPropertiesOverlay.${FormFieldName.PREMIUM}.tooltip`) ? {
                text: this.context.i18nProvider.get(`productPropertiesOverlay.${FormFieldName.PREMIUM}.tooltip`)
            } as InfoButton : undefined}
            onChange={this.changeMagicNumber}
            key={this.state.magicNumber + FormFieldName.PREMIUM}
            required={this.productPropertiesFieldConfigurations[FormFieldName.PREMIUM].required}
            ref={instance => this.productPropertiesFieldConfigurations[FormFieldName.PREMIUM].ref = instance || undefined} />
    }

    renderJobBranchField() {
        assert(this.state.productProperties);
        const productIsForLawyer = this.state.productProperties.productType === ProductType.FOR_LAWYER;
        const productIsForOther = this.state.productProperties.productType === ProductType.FOR_OTHER;

        if (!(productIsForLawyer || productIsForOther)) {
            return <></>
        }

        const productHasCorrespondingProduct = productIsForOther
            ? this.context.utilityProvider.getForLawyerProductFromProductProperties(this.state.productProperties)
            : productIsForLawyer
                ? this.context.utilityProvider.getForOtherProductFromProductProperties(this.state.productProperties)
                : undefined;

        if (!productHasCorrespondingProduct) {
            return <></>
        }

        return <FormFieldRadio name={FormFieldName.JOBBRANCH}
            id={this.state.magicNumber + FormFieldName.JOBBRANCH}
            label={this.context.i18nProvider.get(`productPropertiesOverlay.${FormFieldName.JOBBRANCH}.label`)}
            selected={String(productIsForOther)}
            options={(this.context.i18nProvider.get(`productPropertiesOverlay.${FormFieldName.JOBBRANCH}.choices`, {
                returnObjects: true,
            }) as unknown as Option[])}
            isInPPO={true}
            info={this.context.i18nProvider.exists(`productPropertiesOverlay.${FormFieldName.JOBBRANCH}.tooltip`) ? {
                text: this.context.i18nProvider.get(`productPropertiesOverlay.${FormFieldName.JOBBRANCH}.tooltip`)
            } as InfoButton : undefined}
            onChange={this.changeMagicNumber}
            key={this.state.magicNumber + FormFieldName.JOBBRANCH}
            required={this.productPropertiesFieldConfigurations[FormFieldName.JOBBRANCH].required}
            ref={instance => this.productPropertiesFieldConfigurations[FormFieldName.JOBBRANCH].ref = instance || undefined} />
    }

    info(): React.ReactNode {
        const formFieldNumberOfLicenses = this.state.productProperties?.configOptions?.find(
            formField => formField.name === FormFieldName.NUMBEROFLICENCES
        );

        if ((formFieldNumberOfLicenses as ProductPropertiesSelect).choices.length === 1) {
            return <FormField key={`productPropertiesOverlay.${FormFieldName.NUMBEROFLICENCES}.onlyOneLicense`}
                isInPPO={true}>
                <FormFieldInfoMessage label={this.context.i18nProvider.get(`productPropertiesOverlay.${FormFieldName.NUMBEROFLICENCES}.onlyOneLicense`) || ''} />
            </FormField>
        }

        return <></>;
    }

    renderProductVariant(): React.ReactNode {
        if (
            this.state.productProperties?.productType !== ProductType.VARIANT
            || !(this.state.productProperties?.relatedProducts && this.state.productProperties?.relatedProducts.length >= 0)) {
            return <></>;
        }

        const options: Option[] = [];
        options.push({
            label: this.state.productProperties?.title || 'Ihre Auswahl',
            value: this.state.productProperties?.magicNumber || ''
        });
        for (let i = 0; i < this.state.productProperties?.relatedProducts.length || 0; i++) {
            const element = this.state.productProperties?.relatedProducts[i];
            options.push({
                label: element.title || '', // Title soll aus relatedProducts kommen
                value: element.magicNumber,
            });
        }

        return <FormFieldSelect
            name={FormFieldName.PRODUCTVARIANT}
            id={this.state.magicNumber + FormFieldName.PRODUCTVARIANT}
            options={options.sort((optionA, optionB) => optionA.label.localeCompare(optionB.label))}
            key={this.state.magicNumber + FormFieldName.PRODUCTVARIANT}
            label={this.context.i18nProvider.get(`productPropertiesOverlay.${FormFieldName.PRODUCTVARIANT}.label`) || ''}
            onChangeMagicNumber={this.changeMagicNumber}
            selected={this.state.productProperties?.magicNumber || ''}
            info={this.context.i18nProvider.exists(`productPropertiesOverlay.${FormFieldName.PRODUCTVARIANT}.tooltip`) ? {
                text: this.context.i18nProvider.get(`productPropertiesOverlay.${FormFieldName.PRODUCTVARIANT}.tooltip`)
            } as InfoButton : undefined}
            required={this.productPropertiesFieldConfigurations[FormFieldName.PRODUCTVARIANT].required}
            ref={instance => this.productPropertiesFieldConfigurations[FormFieldName.PRODUCTVARIANT].ref = instance || undefined}
        />;
    }

    getTextPriceContent() {
        const selectValue = this.state.paymentPeriod;
        let result = '' as string;
        let dividerMonthlyPrice = 0;

        switch (selectValue) {
            case '1M':
                result = this.context.i18nProvider.get(`productPropertiesOverlay.${FormFieldName.PAYMENTPERIOD}.periods.${selectValue}`) || '';
                dividerMonthlyPrice = 1;
                break;
            case '3M':
                result = this.context.i18nProvider.get(`productPropertiesOverlay.${FormFieldName.PAYMENTPERIOD}.periods.${selectValue}`) || '';
                dividerMonthlyPrice = 3;
                break;
            case '6M':
                result = this.context.i18nProvider.get(`productPropertiesOverlay.${FormFieldName.PAYMENTPERIOD}.periods.${selectValue}`) || '';
                dividerMonthlyPrice = 6;
                break;
            case '12M':
                result = this.context.i18nProvider.get(`productPropertiesOverlay.${FormFieldName.PAYMENTPERIOD}.periods.${selectValue}`) || '';
                dividerMonthlyPrice = 12;
                break;
        }

        if (result !== 'Monat') {
            const priceReplacer = (this.props.textPrice
                ? this.props.textPrice
                : this.context.i18nProvider.get('productPropertiesOverlay.textPrice') || "")
                .replace('{{pricepermonth}}', (this.state.price.price / dividerMonthlyPrice).toLocaleString('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                    useGrouping: true
                }) + ' €')

            const paymentPeriodReplacer = (priceReplacer || "")
                .replace('{{paymentperiod}}', result);

            return paymentPeriodReplacer;
        } else {
            return this.context ? this.context.i18nProvider.get('productPropertiesOverlay.textPriceMonthly') as unknown as string : "";
        }
    }

    render(): React.ReactNode {
        if (this.state.magicNumber !== '') {
            return (
                <section className={"product-properties-overlay" + (this.state.isTestProduct ? 'product-properties-overlay--priceless' : '')} data-testid="ProductPropertiesOverlay">
                    <div className="product-properties-overlay__header textblock"
                        dangerouslySetInnerHTML={{
                            __html: '<h2>'
                                + (this.state.isTestProduct ? this.context.i18nProvider.get('productPropertiesOverlay.testProduct.title') : this.context.i18nProvider.get('productPropertiesOverlay.title'))
                                + '</h2>'
                                + (!this.context.globalStates.isDiscountable(this.state.productProperties as ProductProperties) && this.state.isTestProduct ? this.context.i18nProvider.get('productPropertiesOverlay.testProduct.textIntro') : (this.context.globalStates.isDiscountable(this.state.productProperties as ProductProperties) && this.state.isTestProduct ? this.context.i18nProvider.get('productPropertiesOverlay.testProduct.textIntroDiscount') : ''))
                        }}>
                    </div>
                    {
                        !this.state.isTestProduct && <form className="form" onSubmit={this.handleAddToCart} ref={this.formElement} id={this.formId} noValidate={true}>
                            <div className="form__inner">
                                <div className="form__content">
                                    <div className="form__main-content">
                                        <fieldset className="form__fieldset">
                                            <div className="form__fieldset-content">
                                                <fieldset className="form__group">
                                                    <div className="form__row row">
                                                        <FormFieldHidden id='magicNumber' value={this.state.magicNumber} />
                                                        {this.state.productProperties && this.renderJobBranchField()}
                                                        {this.state.productProperties && this.renderProductVariant()}
                                                        {this.state.productProperties && this.createFieldIfExist()}
                                                        {this.state.productProperties && this.renderPremiumField()}
                                                        {this.state.productProperties && this.info()}
                                                    </div>
                                                </fieldset>
                                            </div>
                                        </fieldset>
                                    </div>
                                </div>
                            </div>
                        </form>
                    }
                    <div className="product-properties-overlay__bar"></div>
                    <div className="product-properties-overlay__price-grid">
                        {
                            this.context.globalStates.isDiscountable(this.state.productProperties as ProductProperties) && this.state.isTestProduct && (this.props.textOutro || (this.context.i18nProvider.get('productPropertiesOverlay.testProduct.textOutroDiscount')))
                                ? <><div className="textblock product-properties-overlay__text-outro" dangerouslySetInnerHTML={{
                                    __html: this.props.textOutro ? this.props.textOutro : this.context.i18nProvider.get('productPropertiesOverlay.testProduct.textOutroDiscount') + ''
                                }}></div>
                                <div className="textblock product-properties-overlay__test-select">{this.state.productProperties && this.createFieldIfExist()}</div></>
                                : null
                        }
                        {
                            !this.context.globalStates.isDiscountable(this.state.productProperties as ProductProperties) && this.state.isTestProduct && (this.props.textOutro || (this.context.i18nProvider.get('productPropertiesOverlay.testProduct.textOutro')))
                                ? <><div className="textblock product-properties-overlay__text-outro" dangerouslySetInnerHTML={{
                                    __html: this.props.textOutro ? this.props.textOutro : this.context.i18nProvider.get('productPropertiesOverlay.testProduct.textOutro') + ''
                                }}></div>
                                <div className="textblock product-properties-overlay__test-select">{this.state.productProperties && this.createFieldIfExist()}</div></>
                                : null
                        }
                        {
                            !this.state.isTestProduct && <div className="textblock product-properties-overlay__text-price"
                                dangerouslySetInnerHTML={{
                                    __html: (this.getTextPriceContent())
                                }}>
                            </div>
                        }
                        {
                            !this.state.isTestProduct && (this.props.priceInfo || (this.context.i18nProvider.exists('generalRessources.contractInformation')))
                                ? <div className="product-properties-overlay__info" data-tooltip={false}>
                                    <span className="icon_error-circle_alt"></span>
                                    {
                                        (this.context.i18nProvider.exists('generalRessources.contractInformation.label')) || this.props.priceInfo?.label
                                            ? <span className="product-properties-overlay__info-label">
                                                {(this.context.i18nProvider.get('generalRessources.contractInformation.label')) || this.props.priceInfo?.label}
                                            </span>
                                            : null
                                    }
                                    <div className="product-properties-overlay__info-tooltip"
                                        data-tooltip-content={false}>
                                        <span className="icon_error-circle_alt"></span>
                                        <div className="textblock" dangerouslySetInnerHTML={{
                                            __html: (this.context.i18nProvider.get('generalRessources.contractInformation.text'))
                                                || this.props.priceInfo?.text || ""
                                        }}></div>
                                        <span className="cta-overlay-button__close-btn-icon"></span>
                                    </div>
                                </div>
                                : null
                        }
                        {
                            !this.state.isTestProduct && <div className="product-properties-overlay__price-container">
                                {/* <span className="product-properties-overlay__price-prefix">{this.props.pricePrefix}</span> */}
                                <em className="product-properties-overlay__price">{this.state.price.price?.toLocaleString('de-DE', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                    useGrouping: true
                                }) + ' €'}</em>
                            </div>
                        }
                        <div className="product-properties-overlay__button-container">
                            {
                                this.state.isTestProduct
                                    ? (this.props.showBuyButton || (this.props.showBuyButton === undefined && !this.props.isDirectPurchase))
                                    && <button className="cta-button cta-button--button-red"
                                        ref={this.cartButton}
                                        type="button"
                                        onClick={this.handleBuyOfTestProduct}>
                                        {this.state.updateProductInformation ? this.context.i18nProvider.get('productPropertiesOverlay.shoppingCardRefresh') : this.props.primaryButton || (this.context.i18nProvider.get('productPropertiesOverlay.primaryButton'))}
                                    </button>
                                    : (this.props.showBuyButton || (this.props.showBuyButton === undefined && !this.props.isDirectPurchase))
                                    && (this.props.primaryButton || (this.context.i18nProvider.get('productPropertiesOverlay.primaryButton')))
                                    && <button className="cta-button cta-button--button-red"
                                        ref={this.cartButton}
                                        type="submit"
                                        form={this.formElement.current?.id}
                                        onClick={this.submitHandler}>
                                        {this.state.updateProductInformation ? this.context.i18nProvider.get('productPropertiesOverlay.shoppingCardRefresh') : this.props.primaryButton || (this.context.i18nProvider.get('productPropertiesOverlay.primaryButton'))}
                                    </button>
                            }
                            {
                                this.props.isDirectPurchase &&
                                <button className="cta-button cta-button--button-red"
                                    type="button"
                                    onClick={this.handleDirectPurchase}>
                                    {this.context.i18nProvider.get('productPropertiesOverlay.directPurchaseButton')}
                                </button>
                            }
                        </div>
                    </div>
                </section >
            );
        }
        return (
            <section className={"product-properties-overlay"} data-testid="ProductPropertiesOverlay">
                <div className="product-properties-overlay__header textblock"
                    dangerouslySetInnerHTML={{
                        __html: '<h2>'
                            + this.context.i18nProvider.get('productPropertiesOverlay.fallback.title')
                            + '</h2>'
                            + this.context.i18nProvider.get('productPropertiesOverlay.fallback.text')
                    }}>
                </div>
            </section>
        )
    }
}
